import ExcelJS from 'exceljs';
import type { Company, Party, Quotation, QuotationItem } from '../types';

export async function generateQuotationExcel(
  quotation: Quotation,
  company: Company,
  party: Party,
  items: QuotationItem[],
  repName?: string
): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Sai Krishna Industries';
  workbook.lastModifiedBy = 'Sai Krishna Quotation System';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet('Quotation', {
    pageSetup: { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 1 },
    views: [{ showGridLines: true }],
  });

  // Set column widths
  sheet.columns = [
    { key: 'colA', width: 8 },  // S.No
    { key: 'colB', width: 34 }, // Item Code / Details
    { key: 'colC', width: 28 }, // Description / Terms
    { key: 'colD', width: 14 }, // Qty / Labels
    { key: 'colE', width: 16 }, // Rate
    { key: 'colF', width: 18 }, // Amount
  ];

  // Styling helper constants
  const thinBorder: Partial<ExcelJS.Borders> = {
    top: { style: 'thin', color: { argb: 'FFBFBFBF' } },
    left: { style: 'thin', color: { argb: 'FFBFBFBF' } },
    bottom: { style: 'thin', color: { argb: 'FFBFBFBF' } },
    right: { style: 'thin', color: { argb: 'FFBFBFBF' } },
  };

  const headerFill: ExcelJS.Fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFF2F4F7' },
  };

  const primaryFill: ExcelJS.Fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF1E3A8A' },
  };

  let rowIdx = 1;

  // Row 1: GST No & HSN Code
  sheet.mergeCells(`A${rowIdx}:C${rowIdx}`);
  sheet.getCell(`A${rowIdx}`).value = `GST NO: ${company.gst || 'N/A'}`;
  sheet.getCell(`A${rowIdx}`).font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF374151' } };

  sheet.mergeCells(`D${rowIdx}:F${rowIdx}`);
  sheet.getCell(`D${rowIdx}`).value = `HSN: ${company.hsn_codes?.join(', ') || '87169090'}`;
  sheet.getCell(`D${rowIdx}`).font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF374151' } };
  sheet.getCell(`D${rowIdx}`).alignment = { horizontal: 'right' };
  rowIdx += 1;

  // Row 2: Company Legal Name (Large Title)
  sheet.mergeCells(`A${rowIdx}:F${rowIdx}`);
  const titleCell = sheet.getCell(`A${rowIdx}`);
  titleCell.value = company.name.toUpperCase();
  titleCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: 'FF1E3A8A' } };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
  sheet.getRow(rowIdx).height = 25;
  rowIdx += 1;

  // Row 3: Company Address
  if (company.address) {
    sheet.mergeCells(`A${rowIdx}:F${rowIdx}`);
    const addrCell = sheet.getCell(`A${rowIdx}`);
    addrCell.value = company.address;
    addrCell.font = { name: 'Calibri', size: 9, color: { argb: 'FF4B5563' } };
    addrCell.alignment = { horizontal: 'center' };
    rowIdx += 1;
  }

  // Row 4: Contact & Email
  sheet.mergeCells(`A${rowIdx}:F${rowIdx}`);
  const contactCell = sheet.getCell(`A${rowIdx}`);
  contactCell.value = `Contact: ${company.phone || 'N/A'} | Email: ${company.email || 'N/A'}`;
  contactCell.font = { name: 'Calibri', size: 9, color: { argb: 'FF4B5563' } };
  contactCell.alignment = { horizontal: 'center' };
  rowIdx += 1;

  // Row 5: "QUOTATION" banner
  sheet.mergeCells(`A${rowIdx}:F${rowIdx}`);
  const bannerCell = sheet.getCell(`A${rowIdx}`);
  bannerCell.value = 'QUOTATION';
  bannerCell.fill = primaryFill;
  bannerCell.font = { name: 'Calibri', size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
  bannerCell.alignment = { horizontal: 'center', vertical: 'middle' };
  sheet.getRow(rowIdx).height = 22;
  rowIdx += 2;

  // Customer Party (Left) vs Quotation Details (Right)
  sheet.getCell(`A${rowIdx}`).value = 'CUSTOMER DETAILS (M/S)';
  sheet.getCell(`A${rowIdx}`).font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF1E3A8A' } };

  sheet.getCell(`D${rowIdx}`).value = 'QUOTATION DETAILS';
  sheet.getCell(`D${rowIdx}`).font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF1E3A8A' } };
  rowIdx += 1;

  // Customer Name & Quote No
  sheet.mergeCells(`A${rowIdx}:C${rowIdx}`);
  sheet.getCell(`A${rowIdx}`).value = party.name;
  sheet.getCell(`A${rowIdx}`).font = { name: 'Calibri', size: 10, bold: true };

  sheet.getCell(`D${rowIdx}`).value = 'Quotation No.:';
  sheet.getCell(`D${rowIdx}`).font = { name: 'Calibri', size: 9, bold: true };
  sheet.mergeCells(`E${rowIdx}:F${rowIdx}`);
  sheet.getCell(`E${rowIdx}`).value = quotation.quotation_number;
  sheet.getCell(`E${rowIdx}`).font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FF1E3A8A' } };
  rowIdx += 1;

  // Address 1 & Quote Date
  sheet.mergeCells(`A${rowIdx}:C${rowIdx}`);
  sheet.getCell(`A${rowIdx}`).value = party.address1 || '';
  sheet.getCell(`A${rowIdx}`).font = { name: 'Calibri', size: 9 };

  sheet.getCell(`D${rowIdx}`).value = 'Quotation Date:';
  sheet.getCell(`D${rowIdx}`).font = { name: 'Calibri', size: 9 };
  sheet.mergeCells(`E${rowIdx}:F${rowIdx}`);
  sheet.getCell(`E${rowIdx}`).value = quotation.quotation_date;
  sheet.getCell(`E${rowIdx}`).font = { name: 'Calibri', size: 9 };
  rowIdx += 1;

  // Address 2 & Query No
  sheet.mergeCells(`A${rowIdx}:C${rowIdx}`);
  sheet.getCell(`A${rowIdx}`).value = party.address2 || '';
  sheet.getCell(`A${rowIdx}`).font = { name: 'Calibri', size: 9 };

  sheet.getCell(`D${rowIdx}`).value = 'Query Ref No.:';
  sheet.getCell(`D${rowIdx}`).font = { name: 'Calibri', size: 9 };
  sheet.mergeCells(`E${rowIdx}:F${rowIdx}`);
  sheet.getCell(`E${rowIdx}`).value = quotation.query_number || '-';
  sheet.getCell(`E${rowIdx}`).font = { name: 'Calibri', size: 9 };
  rowIdx += 1;

  if (quotation.query_date) {
    sheet.getCell(`D${rowIdx}`).value = 'Query Date:';
    sheet.getCell(`D${rowIdx}`).font = { name: 'Calibri', size: 9 };
    sheet.mergeCells(`E${rowIdx}:F${rowIdx}`);
    sheet.getCell(`E${rowIdx}`).value = quotation.query_date;
    sheet.getCell(`E${rowIdx}`).font = { name: 'Calibri', size: 9 };
    rowIdx += 1;
  }

  // Address 3 / City & Kind Attn
  sheet.mergeCells(`A${rowIdx}:C${rowIdx}`);
  sheet.getCell(`A${rowIdx}`).value = party.address3 || '';
  sheet.getCell(`A${rowIdx}`).font = { name: 'Calibri', size: 9 };

  sheet.getCell(`D${rowIdx}`).value = 'Kind Attn.:';
  sheet.getCell(`D${rowIdx}`).font = { name: 'Calibri', size: 9 };
  sheet.mergeCells(`E${rowIdx}:F${rowIdx}`);
  sheet.getCell(`E${rowIdx}`).value = quotation.kind_attn || '-';
  sheet.getCell(`E${rowIdx}`).font = { name: 'Calibri', size: 9 };
  rowIdx += 2;

  // Table Headers
  const headers = ['S.No.', 'Item Code', 'Description', 'Quantity', 'Rate (₹)', 'Amount (₹)'];
  headers.forEach((h, i) => {
    const colLetter = String.fromCharCode(65 + i);
    const cell = sheet.getCell(`${colLetter}${rowIdx}`);
    cell.value = h;
    cell.fill = headerFill;
    cell.font = { name: 'Calibri', size: 10, bold: true };
    cell.border = thinBorder;
    cell.alignment = { horizontal: i === 0 || i === 3 ? 'center' : i >= 4 ? 'right' : 'left', vertical: 'middle' };
  });
  sheet.getRow(rowIdx).height = 24;
  rowIdx += 1;

  // Table Body Rows
  items.forEach((item, idx) => {
    const r = rowIdx;
    sheet.getCell(`A${r}`).value = item.sno || idx + 1;
    sheet.getCell(`B${r}`).value = item.item_code;
    sheet.getCell(`C${r}`).value = item.description || '';
    sheet.getCell(`D${r}`).value = `${item.quantity} Pcs`;
    sheet.getCell(`E${r}`).value = item.rate;
    sheet.getCell(`E${r}`).numFmt = '₹#,##0.00';
    sheet.getCell(`F${r}`).value = item.amount || item.quantity * item.rate;
    sheet.getCell(`F${r}`).numFmt = '₹#,##0.00';

    ['A', 'B', 'C', 'D', 'E', 'F'].forEach((col, i) => {
      const c = sheet.getCell(`${col}${r}`);
      c.font = { name: 'Calibri', size: 9.5 };
      c.border = thinBorder;
      c.alignment = { horizontal: i === 0 || i === 3 ? 'center' : i >= 4 ? 'right' : 'left', vertical: 'middle' };
    });
    rowIdx += 1;
  });

  rowIdx += 1;

  // Terms & Conditions (Left) vs Financial Totals (Right)
  const totalsStart = rowIdx;

  // Left Column: Terms
  sheet.mergeCells(`A${rowIdx}:C${rowIdx}`);
  sheet.getCell(`A${rowIdx}`).value = 'TERMS AND CONDITIONS:';
  sheet.getCell(`A${rowIdx}`).font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF1E3A8A' } };
  rowIdx += 1;

  const termsList = [
    { label: 'Prices', value: quotation.prices_terms || 'EX-WORKS' },
    { label: 'Payment', value: quotation.payment_terms || 'AGAINST PI' },
    { label: 'Dispatch', value: quotation.dispatch_terms || '10-12 DAYS' },
    { label: 'Freight', value: quotation.freight_terms || 'AT YOUR COST' },
    { label: 'Validity', value: quotation.validity_terms || '30 DAYS' },
    { label: 'Insurance', value: quotation.insurance_terms || 'AT YOUR COST' },
    { label: 'Remarks', value: quotation.remarks || 'Standard warranty applies' },
  ];

  termsList.forEach((t) => {
    sheet.getCell(`A${rowIdx}`).value = t.label;
    sheet.getCell(`A${rowIdx}`).font = { name: 'Calibri', size: 8.5, bold: true };
    sheet.mergeCells(`B${rowIdx}:C${rowIdx}`);
    sheet.getCell(`B${rowIdx}`).value = t.value;
    sheet.getCell(`B${rowIdx}`).font = { name: 'Calibri', size: 8.5 };
    rowIdx += 1;
  });

  // Right Column: Financial Summary
  let finRow = totalsStart;
  const financials = [
    { label: 'SUBTOTAL', val: quotation.subtotal || 0, bold: true },
    { label: `Discount ${(quotation.discount_percent || 0).toFixed(2)}%`, val: -(quotation.discount_amount || 0), hide: !(quotation.discount_amount) },
    { label: `P & F ${(quotation.pf_percent || 0).toFixed(2)}%`, val: quotation.pf_amount || 0, hide: !(quotation.pf_amount) },
    { label: 'Other Charges', val: quotation.others || 0, hide: !(quotation.others) },
    { label: `GST ${(quotation.gst_percent || 18).toFixed(2)}%`, val: quotation.gst_amount || 0 },
    { label: 'GRAND TOTAL', val: quotation.total || 0, bold: true, highlight: true },
  ];

  financials.forEach((f) => {
    if (f.hide) return;
    sheet.mergeCells(`D${finRow}:E${finRow}`);
    const lblCell = sheet.getCell(`D${finRow}`);
    lblCell.value = f.label;
    lblCell.font = { name: 'Calibri', size: 9.5, bold: !!f.bold };
    lblCell.alignment = { horizontal: 'right' };

    const valCell = sheet.getCell(`F${finRow}`);
    valCell.value = f.val;
    valCell.numFmt = '₹#,##0.00';
    valCell.font = { name: 'Calibri', size: f.highlight ? 11 : 9.5, bold: !!f.bold, color: f.highlight ? { argb: 'FF1E3A8A' } : undefined };
    valCell.alignment = { horizontal: 'right' };

    if (f.highlight) {
      lblCell.fill = headerFill;
      valCell.fill = headerFill;
      lblCell.border = thinBorder;
      valCell.border = thinBorder;
    }
    finRow += 1;
  });

  rowIdx = Math.max(rowIdx, finRow) + 1;

  // Bank Details (Left) vs Signatory (Right)
  sheet.mergeCells(`A${rowIdx}:C${rowIdx}`);
  sheet.getCell(`A${rowIdx}`).value = 'BANK DETAILS FOR REMITTANCE:';
  sheet.getCell(`A${rowIdx}`).font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF1E3A8A' } };

  sheet.mergeCells(`D${rowIdx}:F${rowIdx}`);
  sheet.getCell(`D${rowIdx}`).value = `FOR ${company.name.toUpperCase()}`;
  sheet.getCell(`D${rowIdx}`).font = { name: 'Calibri', size: 9, bold: true };
  sheet.getCell(`D${rowIdx}`).alignment = { horizontal: 'right' };
  rowIdx += 1;

  sheet.getCell(`A${rowIdx}`).value = `Bank: ${company.bank_name || 'Bank of Baroda'}`;
  sheet.getCell(`A${rowIdx}`).font = { name: 'Calibri', size: 8.5 };
  rowIdx += 1;

  sheet.getCell(`A${rowIdx}`).value = `A/c No.: ${company.account_number || 'N/A'}`;
  sheet.getCell(`A${rowIdx}`).font = { name: 'Calibri', size: 8.5 };
  rowIdx += 1;

  sheet.getCell(`A${rowIdx}`).value = `IFSC: ${company.ifsc_code || 'N/A'}`;
  sheet.getCell(`A${rowIdx}`).font = { name: 'Calibri', size: 8.5 };

  sheet.mergeCells(`D${rowIdx}:F${rowIdx}`);
  sheet.getCell(`D${rowIdx}`).value = 'AUTHORISED SIGNATORY';
  sheet.getCell(`D${rowIdx}`).font = { name: 'Calibri', size: 9, bold: true };
  sheet.getCell(`D${rowIdx}`).alignment = { horizontal: 'right' };
  rowIdx += 1;

  if (repName) {
    sheet.mergeCells(`D${rowIdx}:F${rowIdx}`);
    sheet.getCell(`D${rowIdx}`).value = `(${repName})`;
    sheet.getCell(`D${rowIdx}`).font = { name: 'Calibri', size: 9 };
    sheet.getCell(`D${rowIdx}`).alignment = { horizontal: 'right' };
  }
  rowIdx += 2;

  // Amount in Words Full Banner
  sheet.mergeCells(`A${rowIdx}:F${rowIdx}`);
  const wordsCell = sheet.getCell(`A${rowIdx}`);
  wordsCell.value = `Rupees: ${quotation.amount_in_words || 'N/A'}`;
  wordsCell.font = { name: 'Calibri', size: 9.5, italic: true, bold: true, color: { argb: 'FF1F2937' } };
  wordsCell.fill = headerFill;
  wordsCell.alignment = { horizontal: 'center', vertical: 'middle' };
  sheet.getRow(rowIdx).height = 20;

  // Write buffer and trigger browser download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${quotation.quotation_number.replace(/[^a-zA-Z0-9_-]/g, '_')}.xlsx`;
  anchor.click();
  window.URL.revokeObjectURL(url);
}
